import React, { useContext, useState } from "react";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainButton from "../../components/Elements/MainButton/MainButton";

import AdminCreateNotificationModal from "../../components/AdminPages/AdminCreateNotificationModal";
import AdminDeleteNotificationModal from "../../components/AdminPages/AdminDeleteNotificationModal";
import AdminEditNotificationModal from "../../components/AdminPages/AdminEditNotificationModal";

import { AuthContext } from "../../context/AuthContext";
import { fetchNotificationsAdmin } from "../../services/NotificationService";

import editIcon from "../../assets/data/edit.png";
import deleteIcon from "../../assets/data/delete.png";

// Columns always visible in notifications table
const alwaysVisibleColumns = [
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "user_id", label: "User" },
  { key: "seen", label: "Seen" },
  { key: "created_at", label: "Created" },
];

// Extra columns user can toggle
const optionalColumns = [
  { key: "id", label: "ID" },
  { key: "description", label: "Description" },
];

// Sorting disabled via showSort=false, keep here for future
const sortOptions = [];

// Filters configuration
const filterDefinitions = [
  { key: "name", type: "text" },
  { key: "description", type: "text" },
  {
    key: "type",
    type: "select",
    options: [
      { value: "ok", label: "OK" },
      { value: "warning", label: "Warning" },
      { value: "error", label: "Error" },
      { value: "payment", label: "Payment" },
      { value: "topup", label: "Topup" },
      { value: "mail", label: "Mail" },
      { value: "settings", label: "Settings" },
      { value: "advertisement", label: "Advertisement" },
      { value: "statistic", label: "Statistic" },
    ],
  },
  { key: "user_id", type: "text" },
];

// Custom cell renderers for notifications table
const notificationRenderers = {
  created_at: (item) => {
    if (!item.created_at) return "-";
    return new Date(item.created_at).toLocaleString("pl-PL");
  },
  type: (item) => {
    const types = {
      ok: "OK",
      warning: "Warning",
      error: "Error",
      payment: "Payment",
      topup: "Topup",
      mail: "Mail",
      settings: "Settings",
      advertisement: "Advertisement",
      statistic: "Statistic",
    };
    return types[item.type] || item.type || "-";
  },
  seen: (item) => (item.seen ? "Yes" : "No"),
};

const AdminNotifications = () => {
  const { token } = useContext(AuthContext);

  // Modal visibility and data version state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [editingNotification, setEditingNotification] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Row action handlers
  const handleEditNotification = (notification) => {
    setEditingNotification(notification);
    setShowEditModal(true);
  };

  const handleNotificationUpdated = () => {
    setShowEditModal(false);
    setEditingNotification(null);
    setDataVersion((v) => v + 1);
  };

  const handleDeleteNotification = (notification) => {
    setSelectedNotification(notification);
    setShowDeleteModal(true);
  };

  const handleNotificationDeleted = () => {
    setShowDeleteModal(false);
    setSelectedNotification(null);
    setDataVersion((v) => v + 1);
  };

  // Data loader adapter for DataTable
  const fetchDataFn = ({ skip, limit, filters }) =>
    fetchNotificationsAdmin({
      offset: skip,
      limit,
      filters,
    });

  const handleNotificationCreated = () => {
    setShowCreateModal(false);
    setDataVersion((v) => v + 1);
  };

  const createButton = <MainButton onClick={() => setShowCreateModal(true)}>Create +</MainButton>;

  const notificationRowActions = [
    {
      label: "Edit",
      icon: editIcon,
      onClick: handleEditNotification,
    },
    {
      label: "Delete",
      icon: deleteIcon,
      onClick: handleDeleteNotification,
    },
  ];

  return (
    <div className="admin-notifications-page">
      <PageHeader title="Notifications" description="View all notifications" actions={createButton} />

      <DataTable
        key={dataVersion}
        tableId="admin-notifications"
        fetchDataFunction={fetchDataFn}
        columns={alwaysVisibleColumns}
        optionalColumns={optionalColumns}
        sortOptions={sortOptions}
        filterDefinitions={filterDefinitions}
        customRenderers={notificationRenderers}
        defaultSortBy={null}
        itemKey="id"
        rowActions={notificationRowActions}
        showCheckboxes={false}
        showFilters={true}
        showPageSize={true}
        showSort={false}
      />

      <AdminCreateNotificationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        onCreated={handleNotificationCreated}
      />

      <AdminDeleteNotificationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        notification={selectedNotification}
        onDeleted={handleNotificationDeleted}
      />

      <AdminEditNotificationModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        notification={editingNotification}
        onUpdated={handleNotificationUpdated}
      />
    </div>
  );
};

export default AdminNotifications;
